<template>
    <div class="page">
        <Navigation pageName="team-view" :data="{ team: team }" />

        <!-- LOADER -->
        <div v-if="team == null && loading" class="d-flex flex-justify-center p-10">
            <span class="mif-spinner2 fg-primary ani-spin mif-3x"></span>
        </div>

        <!-- CONTENT -->
        <div v-else-if="team != null && !loading" class="p-5">
            <!-- METADATA CARDS -->
            <div class="row mb-4">
                <div class="w-33 mt-2">
                    <div class="more-info-box card rounded p-2">
                        <div class="content">
                            <h2 class="text-bold mb-0">{{ team.users.length }}</h2>
                            <div>{{ $t("Members") }}</div>
                        </div>
                        <div class="icon"><span class="mif-users"></span></div>
                    </div>
                </div>

                <div class="w-33 mt-2">
                    <div class="more-info-box card rounded p-2">
                        <div class="content">
                            <h2 class="team-lead-name text-bold mb-0">{{ leadUser.firstname }} {{ leadUser.lastname }}</h2>
                            <div>{{ leadUser.username }}</div>
                        </div>
                        <div class="icon"><span class="mif-medal"></span></div>
                    </div>
                </div>
                <div class="w-33 mt-2">
                    <div class="more-info-box card rounded p-2">
                        <div class="content">
                            <h2 class="text-bold mb-0">{{ tickets != null ? tickets.length : "—" }}</h2>
                            <div>{{ $t("Last requests (30d)") }}</div>
                        </div>
                        <div class="icon"><span class="mif-bell"></span></div>
                    </div>
                </div>
            </div>

            <!-- MEMBRI -->
            <h4 class="border-bottom pb-1 mt-4">{{ $t("Members") }}</h4>
            <NoDataAvailable v-if="!team.users || team.users.length == 0" :message="$t('No members yet.')" />
            <table v-else class="striped compact table w-100 bg-white border">
                <thead>
                    <tr>
                        <th>{{ $t("Username") }}</th>
                        <th>{{ $t("Firstname") }}</th>
                        <th>{{ $t("Lastname") }}</th>
                        <th class="text-center">{{ $t("Role") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="m in team.users" :key="m.id_user">
                        <td>
                            <span class="text-bold">{{ $root.userMap[m.id_user].username }}</span>
                            <span v-if="m.is_lead" class="badge inline ml-2 success" data-role="hint" :data-hint-text="$t('Team Leader')">
                                <span class="mif-medal mr-1"></span>{{ $t("Lead") }}
                            </span>
                        </td>
                        <td>{{ $root.userMap[m.id_user].firstname }}</td>
                        <td>{{ $root.userMap[m.id_user].lastname }}</td>
                        <td class="text-center">
                            <span class="badge inline tertiary">{{ $t($root.userMap[m.id_user].role) }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>


            <!-- RECENT REQUESTS -->
            <div class="d-flex flex-align-center mt-4">
                <h4 class="border-bottom pb-1 flex-1 m-0">{{ $t("Recent assistance requests") }}</h4>
                <router-link :to="'/tickets?id_team=' + team.id" class="button flat-button">
                    {{ $t("View all") }} <span class="mif-arrow-right ml-1"></span>
                </router-link>
            </div>
            <div v-if="tickets == null" class="p-3">
                <span class="mif-spinner2 ani-spin fg-primary"></span>
            </div>
            <NoDataAvailable v-else-if="tickets.length == 0" :message="$t('No historical requests')" />
            <table v-else class="striped compact table w-100 bg-white border">
                <thead>
                    <tr>
                        <th>{{ $t("Date") }}</th>
                        <th>{{ $t("Customer") }}</th>
                        <th>{{ $t("Title") }}</th>
                        <th>{{ $t("Status") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="r in tickets" :key="r.id">
                        <td class="text-small">{{ formatDate(r.dt_registered) }}</td>
                        <td>{{ $root.customerMap[r.id_customer] ? $root.customerMap[r.id_customer].company_name : r.id_customer }}</td>
                        <td>{{ r.title || "—" }}</td>
                        <td>
                            <div>
                                <span :class="'badge inline bg-' + TICKET_STATUS_INFO[r.status].bgcolor + ' fg-' + TICKET_STATUS_INFO[r.status].color">
                                    {{ $t(TICKET_STATUS_INFO[r.status].label) }}</span>
                            </div>
                            <div v-if="r.id_team && r.id_user_assigned">
                                <span>{{ $root.teamMap[r.id_team].name }}</span>
                                <span class="mif-arrow-right mx-1"></span>
                                <span>{{ $root.userMap[r.id_user_assigned].username }}</span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import Navigation from '../utils/Navigation';
import NoDataAvailable from '../utils/NoDataAvailable.vue';
import TeamDialog from '../dialogs/ticketing/TeamDialog.vue';
import moment from 'moment';
import { deleteTeam } from '../../utils/entityMethods/team';
import { populateUsefulMaps, TICKET_STATUS, TICKET_STATUS_INFO } from '../../utils/entityMethods/ticket.js';

export default {
    name: "TeamView",
    components: { Navigation, NoDataAvailable },
    props: {
        idTeam: { type: String, required: true }
    },
    data() {
        return {
            TICKET_STATUS_INFO,
            team: null,
            loading: true,
            tickets: null,
            leadUser: null,
            isAdmin: false
        };
    },

    async mounted() {
        if (!this.$root.checkAuth()) return;
        this.$session.setRoomPC(null);
        if (this.$root.socket) this.$root.socket("SETPC", null);

		await this.populateUsefulMaps();

        await this.loadTeam();
        var leadMember = this.team.users.find(m => m.is_lead);
        this.isAdmin = this.$root.authObj.roles == 'Admin';
        this.leadUser = this.$root.userMap[leadMember.id_user];
        this.loadRecentRequests();

        // Aggiornamento dopo che il TeamDialog ha salvato
        this.$root.$on("refreshTeams", this.onTeamRefreshed);
    },
    beforeDestroy() {
        this.$root.$off("refreshTeams", this.onTeamRefreshed);
    },
    watch: {
        idTeam(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.team = null;
                this.tickets = null;
                this.loadTeam();
                this.loadRecentRequests();
            }
        }
    },
    methods: {
        populateUsefulMaps,

        async loadTeam() {
            this.loading = true;
            this.team = await this.$api.getTeam(this.idTeam);
            this.loading = false;
        },
        async loadRecentRequests() {

            const dtFrom = moment().subtract(30, 'days').format("YYYYMMDD") + "000000";
            this.tickets = await this.$api.getTickets({
                id_team: this.idTeam,
                status_in: [TICKET_STATUS.COMPLETED, TICKET_STATUS.RELEASED, TICKET_STATUS.CANCELLED].join(","),
                dt_from: dtFrom,
                limit: 10
            });

        },
        onTeamRefreshed() {
            this.loadTeam();
        },



        // ── FORMATTERS ────────────────────────────────────────
        formatDate(d) {
            if (!d) return "—";
            return moment(d).format("DD/MM/YYYY HH:mm");
        },
        // ── ACTIONS ───────────────────────────────────────────
        deleteTeam

    }
};
</script>

<style scoped>
.flex-1 {
    flex: 1;
}

.more-info-box {
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #e2e8f0;
}

.more-info-box .icon {
    font-size: 32px;
    opacity: 0.4;
}

.team-lead-name {
    font-size: 1.25rem;
    line-height: 1.2;
    overflow-wrap: anywhere;
}
</style>
